import type { CityConfig } from "@/pages/city-template/types";

export const newportOrConfig: CityConfig = {
  city: "Newport",
  state: "Oregon",
  stateAbbr: "OR",
  slug: "newport-or",
  route: "/cities/newport-or",
  title: "Emergency Passport Newport OR | Expedited Passport Lincoln County Oregon",
  metaDescription: "Emergency passport and expedited passport services in Newport, OR — as fast as 24 hours. Fast Passport Center serves Newport, Lincoln City, Toledo, Waldport, and Lincoln County. Registered State Dept. courier. A+ BBB rated. No office visit required.",
  h1: "Emergency & Expedited Passport Services in Newport, Oregon",
  heroSubtitle: "Mexico trip booked and passport expired in Newport? Alaska cruise departing Seattle? Canada family emergency? Fast Passport Center helps Newport and Lincoln County residents get expedited passports as fast as 24 hours — entirely remotely, without the 5-hour drive to Seattle. Oregon's oceanographic hub, home of Oregon Coast Aquarium and Hatfield Marine Science Center — and now fully served for emergency passport processing.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Newport%20Oregon%20Bayfront%20historic%20district%20with%20commercial%20fishing%20boats%20and%20crab%20pots%20on%20the%20docks%2C%20Yaquina%20Bay%20and%20its%20iconic%20bridge%20in%20background%2C%20Pacific%20Ocean%20horizon%20with%20dramatic%20overcast%20sky%2C%20maritime%20working%20waterfront%20atmosphere%20with%20sea%20lions%20visible%20on%20docks%2C%20colorful%20seafood%20restaurants%20and%20fishing%20equipment%2C%20photorealistic%20coastal%20Pacific%20Northwest%20scene%2C%20no%20text%20no%20people&width=1800&height=700&seq=newport-or-hero-01&orientation=landscape",
  heroImageAlt: "Newport Oregon Yaquina Bay waterfront with commercial fishing fleet and bay bridge",
  geoPosition: "44.6368;-124.0534",
  geoPlacename: "Newport, Oregon",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Seattle Passport Agency",
    address: "915 2nd Ave Suite 500, Seattle WA 98174",
    distance: "About 335 miles north — approximately 5.5–6 hours by car",
  },
  nearbyCitiesZips: [
    { name: "Newport", zips: "97365" },
    { name: "Toledo", zips: "97391" },
    { name: "Lincoln City", zips: "97367" },
    { name: "Waldport", zips: "97394" },
    { name: "Depoe Bay", zips: "97341" },
    { name: "Siletz", zips: "97380" },
    { name: "Yachats", zips: "97498" },
    { name: "Florence", zips: "97439" },
    { name: "Corvallis", zips: "97330, 97331, 97333" },
    { name: "Salem", zips: "97301–97317" },
    { name: "Albany", zips: "97321, 97322" },
    { name: "Eugene", zips: "97401–97440" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online from Newport", desc: "Complete your emergency or expedited passport order from Newport — no drive to Eugene, Salem, or Seattle required." },
    { icon: "ri-truck-line", title: "Ship Overnight via Corvallis or Salem", desc: "Corvallis is about 50 miles east of Newport via US-20 — the fastest inland shipping route for central coast passport submissions." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application reviewed before submission — essential for Newport and Lincoln County residents far from major passport offices." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Submission Access", desc: "We bypass the public appointment backlog with reserved Seattle Passport Agency slots — no 5.5-hour drive required." },
  ],
  testimonials: [
    {
      name: "Karen T.",
      location: "Newport, OR",
      rating: 5,
      text: "HMSC researcher here — needed a passport renewed for an international marine science conference in Japan. Fast Passport Center got it done in 8 business days. I shipped from the Corvallis FedEx on my weekly commute. Incredible service for Newport coast residents.",
      service: "Expedited Passport Renewal",
    },
    {
      name: "Bob R.",
      location: "Newport, OR",
      rating: 5,
      text: "My wife and I had an Alaska cruise booked from Seattle — 2 weeks out when I found my passport was expired. Fast Passport Center came through with a 9-day renewal. I shipped from Salem on a shopping day. They kept us informed every step. Incredible.",
      service: "Expedited Passport Renewal",
    },
    {
      name: "Michelle G.",
      location: "Toledo, OR",
      rating: 5,
      text: "Toledo, Oregon is not exactly close to anything. Fast Passport Center made the whole process work — their checklist was perfect, and shipping from Corvallis FedEx was easy enough on my Winco shopping day. Got my passport back in 7 days. Fantastic service.",
      service: "Expedited Passport Renewal",
    },
  ],
  facilities: [
    {
      id: "newport-post-main",
      name: "Newport Post Office",
      type: "USPS Passport Acceptance",
      address: "310 SW 2nd St",
      city: "Newport",
      state: "OR",
      zip: "97365",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "Downtown Newport — primary passport acceptance for Newport and Toledo residents",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Primary passport acceptance for Newport and Lincoln County residents. Appointment required. Availability can be limited during summer — book early.",
      icon: "ri-mail-line",
    },
    {
      id: "lincoln-county-clerk",
      name: "Lincoln County Clerk's Office",
      type: "County Clerk Passport Acceptance",
      address: "225 W Olive St",
      city: "Newport",
      state: "OR",
      zip: "97365",
      phone: "(541) 265-4131",
      hours: "Mon–Fri 8am–5pm",
      distance: "Newport — Lincoln County Courthouse, downtown Newport",
      website: "https://www.co.lincoln.or.us/clerk",
      websiteLabel: "Lincoln County Clerk",
      notes: "Lincoln County Clerk accepts passport applications. Often better appointment availability than the post office — recommended for urgent Newport-area cases.",
      icon: "ri-government-line",
    },
    {
      id: "waldport-post",
      name: "Waldport Post Office",
      type: "USPS Passport Acceptance",
      address: "340 Maple St",
      city: "Waldport",
      state: "OR",
      zip: "97394",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Waldport — about 15 miles south of Newport via US-101",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Very small facility — limited appointment availability. Newport or Yachats preferred for southern Lincoln County residents.",
      icon: "ri-mail-line",
    },
    {
      id: "lincoln-city-post",
      name: "Lincoln City Post Office",
      type: "USPS Passport Acceptance",
      address: "1501 SE Devil's Lake Rd",
      city: "Lincoln City",
      state: "OR",
      zip: "97367",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "Lincoln City — about 25 miles north of Newport via US-101",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Lincoln City post office is an alternative for northern Lincoln County residents needing better appointment availability.",
      icon: "ri-mail-line",
    },
    {
      id: "corvallis-post",
      name: "Corvallis Post Office",
      type: "USPS Passport Acceptance",
      address: "311 SW 2nd St",
      city: "Corvallis",
      state: "OR",
      zip: "97333",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "Corvallis — about 50 miles east via US-20 (1 hour) — significantly better appointment availability",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Corvallis is the recommended inland acceptance facility for Newport residents — 50 miles east on US-20. Much better appointment availability than any Lincoln County location. Corvallis FedEx Office is also the recommended overnight shipping location.",
      icon: "ri-mail-line",
    },
    {
      id: "yachats-post",
      name: "Yachats Post Office",
      type: "USPS Passport Acceptance",
      address: "1 W 3rd St",
      city: "Yachats",
      state: "OR",
      zip: "97498",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Yachats — about 25 miles south of Newport via US-101",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Very small facility — minimal appointment availability. Newport or Corvallis strongly preferred.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport from Newport, Oregon?",
      a: "Yes. Fast Passport Center handles emergency passport processing for Newport and Lincoln County residents as fast as 24 hours. We serve central coast Oregon entirely remotely — ship overnight from Corvallis FedEx (50 miles east on US-20) or Salem, and we handle the Seattle agency submission.",
    },
    {
      q: "How fast can I get an expedited passport from Newport, OR?",
      a: "Expedited passport processing from Newport takes 2–9 business days through Fast Passport Center. Most adult renewals don't require in-person acceptance — ship overnight from Corvallis FedEx on your next Winco shopping run east on US-20.",
    },
    {
      q: "Where is the passport office in Newport, Oregon?",
      a: "Newport's primary passport acceptance facilities are the Newport Post Office (310 SW 2nd St) and the Lincoln County Clerk (225 W Olive St), both in downtown Newport. Corvallis (50 miles east on US-20) is strongly recommended for better appointment availability.",
    },
    {
      q: "How far is Newport from the Seattle Passport Agency?",
      a: "The Seattle Passport Agency is approximately 335 miles north of Newport — about 5.5–6 hours by car. For most urgent travel, Fast Passport Center serves Newport entirely remotely in 2–9 business days without the drive.",
    },
    {
      q: "Can I renew my passport from Newport without visiting an office?",
      a: "Yes. Adult passport renewals can be done entirely by mail — no in-person acceptance visit required. Ship overnight from Corvallis FedEx on your next US-20 inland trip, and Fast Passport Center handles the Seattle agency submission.",
    },
    {
      q: "What airport do Newport residents use for international travel?",
      a: "Portland International Airport (PDX) is the primary international hub for Newport residents — about 130 miles north via US-20 and I-5 or US-101 north (about 2.5 hours). Eugene Airport (EUG) is also accessible about 80 miles south via US-20. PDX is preferred for direct international flights.",
    },
    {
      q: "Do HMSC or Oregon Coast Aquarium researchers get expedited passports?",
      a: "Yes. Hatfield Marine Science Center and Oregon Coast Aquarium researchers in Newport travel internationally for conferences, research partnerships, and fieldwork. Fast Passport Center regularly helps marine science professionals get expedited passports with research timeline awareness.",
    },
    {
      q: "Can I get a child passport from Newport?",
      a: "Yes. Child passports require an in-person acceptance visit at the Newport Post Office or Lincoln County Clerk — both parents must typically be present or provide notarized consent. Fast Passport Center provides the child passport checklist and expedites after the acceptance step.",
    },
    {
      q: "Is US-20 from Newport to Corvallis the best route for passport shipping?",
      a: "Yes. US-20 connects Newport to Corvallis in about 50 miles (1 hour) through the Coast Range — the fastest inland connection for Newport residents needing to access Corvallis FedEx. Fast Passport Center includes specific shipping location guidance with your personalized checklist.",
    },
    {
      q: "Can commercial fishermen in Newport get expedited passports?",
      a: "Yes. Newport is Oregon's largest commercial fishing port, and fishing industry professionals travel internationally for Alaska permits, BC joint management, Pacific Rim ports, and international fisheries meetings. Fast Passport Center handles rapid expedited passports for Newport's commercial fishing community.",
    },
  ],
  photoLocations: [
    { name: "Walmart — Newport", address: "140 SE Harney St, Newport, OR 97365", hours: "Daily 6 AM – 11 PM" },
    { name: "Fred Meyer — Newport", address: "603 Coast Hwy, Newport, OR 97365", hours: "Daily 7 AM – 11 PM" },
    { name: "Walgreens — Newport", address: "1611 N Coast Hwy, Newport, OR 97365", hours: "Daily 8 AM – 10 PM" },
    { name: "FedEx Office — Corvallis", address: "2317 NW 9th St, Corvallis, OR 97330", hours: "Mon–Fri 7 AM – 10 PM, Sat–Sun 8 AM – 6 PM" },
    { name: "The UPS Store — Corvallis", address: "1571 SW 53rd St, Corvallis, OR 97333", hours: "Mon–Fri 8 AM – 6:30 PM, Sat 9 AM – 4 PM" },
    { name: "Walgreens — Corvallis", address: "1945 NW 9th St, Corvallis, OR 97330", hours: "Daily 8 AM – 10 PM" },
    { name: "FedEx Office — Salem", address: "3015 Commercial St SE, Salem, OR 97302", hours: "Mon–Fri 7 AM – 10 PM, Sat–Sun 9 AM – 6 PM" },
    { name: "Costco Photo — Corvallis", address: "140 NW Elks Dr, Corvallis, OR 97330", hours: "Mon–Fri 10 AM – 8:30 PM, Sat–Sun 9:30 AM – 6 PM" },
    { name: "Walmart Photo — Corvallis", address: "500 NW Circle Blvd, Corvallis, OR 97330", hours: "Daily 7 AM – 11 PM" },
    { name: "CVS — Eugene", address: "Multiple Eugene locations", hours: "Daily 7 AM – 10 PM" },
  ],
  dmvLocations: [
    {
      name: "Newport Driver & Motor Vehicle Services",
      address: "3400 South Coast Hwy, Newport, OR 97365",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Newport DMV — driver licenses, REAL ID, and ID cards for Newport and Lincoln County south residents.",
      featured: true,
    },
    {
      name: "Lincoln City Driver & Motor Vehicle Services",
      address: "1501 SE Devil's Lake Rd, Lincoln City, OR 97367",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Lincoln City DMV — serves northern Lincoln County including Lincoln City, Depoe Bay, and Siletz.",
      featured: false,
    },
    {
      name: "Corvallis Driver & Motor Vehicle Services",
      address: "NW Tyler Ave, Corvallis, OR 97330",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Corvallis DMV — inland alternative for Newport residents on US-20 shopping runs to Corvallis.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "PDX",
      name: "Portland International Airport",
      distance: "Primary international hub — about 130 miles north via US-20 and I-5 (2.5 hours)",
      address: "7000 NE Airport Way, Portland, OR 97218",
      note: "Portland International Airport is the primary international departure hub for Newport residents. US-20 east to Corvallis, then north on I-5 or US-99W to PDX. PDX serves direct international routes to Amsterdam, London, Tokyo, Vancouver BC, and Mexico City.",
      flightUrl: "https://www.flypdx.com/",
      flightLabel: "PDX Airport Website",
      featured: true,
    },
    {
      code: "EUG",
      name: "Eugene Airport",
      distance: "About 80 miles south via US-20 and OR-34 or US-101 south (1.5 hours)",
      address: "28801 Douglas Dr, Eugene, OR 97402",
      note: "Eugene Airport offers connections to San Francisco, Los Angeles, Denver, and Seattle for onward international travel — a useful alternative for Newport residents heading south.",
      flightUrl: "https://www.eugeneairport.com/",
      flightLabel: "Eugene Airport Website",
      featured: false,
    },
    {
      code: "OTH",
      name: "Southwest Oregon Regional Airport",
      distance: "About 150 miles south in North Bend/Coos Bay (2.5 hours)",
      address: "1100 Airport Ln, North Bend, OR 97459",
      note: "Regional airport south of Newport — domestic connections only. Portland International (PDX) is preferred for all Newport-area international travel.",
      flightUrl: "https://www.cooscountyairportdistrict.com/",
      flightLabel: "OTH Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport — Newport & Lincoln County",
      description: "International marine science conference coming up? Mexico cruise leaving from LA? Canada family emergency? Fast Passport Center handles emergency passport processing for Newport residents as fast as 24 hours — entirely remotely, no Seattle road trip.",
      bullets: ["As fast as 24 hours processing", "Reserved Seattle agency submission", "Dedicated agent and live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Expedited Passport Renewal",
      description: "Passport expired during Newport coast years? Newport residents renew fast — ship overnight from Corvallis FedEx (50 miles east on US-20) on your next shopping run, and we expedite the submission. Back in 2–9 business days.",
      bullets: ["Fastest renewal for Newport and coast residents", "Ship overnight from Corvallis on US-20 run", "Professional document review included"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost Passport Replacement",
      description: "Passport lost in Newport or before international travel? We guide Lincoln County residents through DS-64 and expedited replacement — entirely remotely.",
      bullets: ["DS-64 loss/theft reporting guidance", "Expedited replacement timelines", "Pre-submission document review"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport — Newport & Coast Families",
      description: "Family Mexico or Canada trip? Child passports require an in-person acceptance step at the Newport Post Office or County Clerk. We guide you and expedite from there.",
      bullets: ["Both parents typically required to consent", "Newport acceptance facility guidance included", "Expedited processing after acceptance step"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-add-line",
      title: "First-Time Passport Applications",
      description: "First passport from Newport? In-person acceptance at the Newport Post Office or Lincoln County Clerk is required. We guide you through every step and expedite from there.",
      bullets: ["Step-by-step Newport acceptance guidance", "Photo location guide for Newport area", "Expedited processing after acceptance step"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-truck-line",
      title: "Registered State Dept. Courier Service",
      description: "Fast Passport Center is a registered U.S. Department of State courier — not a reseller. We submit through reserved agency channels at 30–100% lower prices than retail services.",
      bullets: ["State Dept. registered courier", "Best price guarantee", "Not affected by government shutdowns"],
      badge: "Registered Courier",
      link: "https://fastpassportcenter.com/checkout/",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change & Specialty Passport Services",
      description: "Name change after marriage, damaged passport replacement, and second passports for Newport's marine science community, fishing industry, and frequent travelers.",
      bullets: ["Name change passports", "Damaged passport replacement", "Second passports for research and fishing professionals"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Same-day emergency passport processing for Newport, Oregon residents with urgent international travel needs." },
    { name: "Expedited Passport", description: "Expedited passport processing for Newport and Lincoln County residents in 2–9 business days." },
    { name: "Passport Renewal", description: "Fast passport renewal for Newport, Oregon — ship overnight from Corvallis or Salem." },
    { name: "Lost Passport Replacement", description: "Emergency passport replacement for Newport and Lincoln County travelers with lost or stolen passports." },
    { name: "Child Passport", description: "Expedited child passport services for Newport and central Oregon coast families." },
    { name: "Passport Courier Service", description: "Registered U.S. Department of State courier serving Newport, Lincoln City, and central Oregon coast." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Booking Alaska or Mexico Cruises Without Checking Passport Expiration", description: "Mexico and Alaska cruises from LA and Seattle are among the most popular international trips for Newport-area residents. Cruise final payments are due 90 days before departure — when many discover their passport expired. Check expiration the day you book." },
    { icon: "ri-camera-off-line", title: "Using Low-Quality Passport Photos", description: "Photos from Walmart or Fred Meyer Newport frequently get rejected for lighting or composition issues. Fast Passport Center reviews specifications before submission." },
    { icon: "ri-file-damage-line", title: "Incomplete Forms or Missing Signatures", description: "One missing signature returns your application — costing weeks. Our pre-submission review catches all errors before submission." },
    { icon: "ri-pen-nib-line", title: "Wrong Birth Certificate Format", description: "Short-form birth certificates are rejected. We specify exactly what document format is needed based on your state of birth." },
    { icon: "ri-map-pin-time-line", title: "Underestimating How Remote Newport Is from Passport Resources", description: "Newport is one of Oregon's most isolated cities from major passport acceptance and shipping hubs. Corvallis (50 miles east on US-20) is the best inland resource. Fast Passport Center accounts for this in your timeline estimate." },
    { icon: "ri-calendar-close-line", title: "Waiting Too Long After Booking International Research Travel", description: "HMSC and marine science researchers sometimes book international conference travel on short notice — and discover passport expiration too late. Apply immediately when international research travel is confirmed." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online from Newport", description: "Choose your processing speed based on your travel date. Complete your secure order from Newport in minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "Receive Your Personalized Checklist", description: "We send a personalized checklist — exactly what to gather, with Newport and Corvallis shipping guidance." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Overnight on Your Next US-20 Run", description: "Drive US-20 east to Corvallis (50 miles) — drop off at Corvallis FedEx or UPS Store. Or ship from Salem FedEx via US-20/US-22." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Agency Access", description: "We submit through reserved Seattle Passport Agency slots — no 5.5-hour drive from Newport required." },
    { number: "05", icon: "ri-passport-line", title: "Passport Shipped to Your Newport Door", description: "Your completed passport ships directly to your Newport or Lincoln County address. Live status tracking included." },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Must drive to Seattle — 5.5–6 hours from Newport",
        "Life-or-death emergencies only",
        "Very limited appointment availability",
        "Not practical for Newport OR residents",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Ship overnight from Corvallis (50 miles east)",
        "Document review included",
        "Dedicated agent on your case",
        "Daily status updates",
        "Best price guarantee",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than USPS self-service",
        "Document review included",
        "Dedicated agent",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "Standard mail — no courier access",
        "6–13 weeks; longer in peak summer",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent",
        "Not suitable if travel is under 10 weeks away",
      ],
      highlight: false,
      muted: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — weeks-long wait in Seattle", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Drive 5.5 hours to Seattle from Newport", fpc: "Ship overnight from Corvallis (50 miles east)" },
    { feature: "Processing Speed", agency: "Strict emergency eligibility", fpc: "As fast as 24 hours — multiple tiers" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Coast Accessibility", agency: "Not designed for remote coast residents", fpc: "Remote service — ship from Corvallis" },
    { feature: "Government Shutdowns", agency: "May be impacted", fpc: "Not affected" },
    { feature: "Best Price Guarantee", agency: "Govt. fees + 11–12 hour Seattle round trip", fpc: "Best price guarantee — no travel required" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Lincoln City", route: "/cities/lincoln-city" },
    { name: "Emergency passport help in Corvallis", route: "/cities/corvallis" },
    { name: "Fast passport renewal in Eugene", route: "/cities/eugene" },
    { name: "Rush passport services for Oregon residents", route: "/states/oregon" },
    { name: "Expedited passports statewide in Oregon", route: "/states/oregon" },
  ],
  fpcOfficeNote: "After placing your order, we send your personalized checklist and overnight shipping instructions. Drive US-20 east to Corvallis FedEx (50 miles) on your next shopping run. Or ship from Salem FedEx via US-20/US-22. No in-person visit to our processing facility required.",
  fpcOfficeDistance: "Serving Newport and the central Oregon coast remotely",
  serviceAreaIntro: "Whether you are in Newport, Toledo, Waldport, Depoe Bay, Yachats, Siletz, or anywhere in Lincoln County — Fast Passport Center handles emergency and expedited passports without the Seattle road trip.",
  nearbyCitiesIntro: "We serve all central Oregon coast zip codes including Newport, Toledo, Lincoln City, Waldport, Depoe Bay, Siletz, Yachats, Florence, and inland communities via US-20 including Corvallis, Albany, and Salem.",
  howItWorksIntro: "Getting an emergency or expedited passport from Newport is simpler than a 5.5-hour drive to Seattle. Ship overnight from Corvallis (50 miles east on US-20) on your next shopping run — most coast clients never visit an office.",
  mistakesIntro: "These are the most common mistakes that cause delays for Newport and Lincoln County passport applicants. We review your documents before submission.",
  testimonialsIntro: "Real reviews from Newport, Toledo, and central Oregon coast travelers who needed their passports fast.",
  faqIntro: "The most common questions Newport and Lincoln County residents ask about emergency and expedited passport services.",
  statePageRoute: "/states/oregon",
  statePageName: "Oregon",
  travelTiming: {
    title: "Travel & Passport Timing for Newport and Lincoln County Residents",
    intro: "Newport is the cultural and commercial center of the central Oregon coast — home to Hatfield Marine Science Center, Oregon Coast Aquarium, a major commercial fishing port, and a vibrant arts and outdoor community. US-20 connects Newport to Corvallis (50 miles) and Salem (120 miles) for inland access. These connections and Newport's distinctive community identity create unique international travel patterns.",
    cards: [
      {
        icon: "ri-flask-line",
        title: "Hatfield Marine Science Center & Oregon Coast Aquarium",
        subtitle: "Newport's marine science community travels internationally for research and conferences",
        description: "Hatfield Marine Science Center (HMSC) is one of the Pacific coast's leading marine research institutions — a joint facility of Oregon State University, NOAA, EPA, and other agencies. Newport is home to a significant population of marine scientists, researchers, and graduate students who travel internationally for field research, academic conferences, and institutional partnerships in Japan, New Zealand, Australia, Norway, and throughout the Pacific Rim. These international trips can arise on relatively short notice — a funding approval or conference acceptance can mean a passport is needed within weeks.",
        tip: "HMSC researchers and grad students: check your passport expiration every January. International fieldwork and conference travel can arise with 3–6 weeks notice. Fast Passport Center expedites renewals from Newport in 2–9 business days via Corvallis FedEx.",
      },
      {
        icon: "ri-anchor-line",
        title: "Commercial Fishing Industry Travel",
        subtitle: "Newport is Oregon's largest commercial fishing port — industry travel is international",
        description: "Newport is Oregon's largest and most active commercial fishing port — the hub of the Oregon commercial crab, tuna, groundfish, and salmon industries. Commercial fishing professionals in Newport travel internationally for industry meetings, Alaska fisheries operations, BC joint management agreements, Pacific Rim port calls, and international seafood industry conferences. These trips can arise suddenly — a Japan or BC meeting with a week's notice. Fast Passport Center handles rapid expedited passports for Newport's commercial fishing community.",
        tip: "Commercial fishing professionals in Newport should maintain a passport with 18+ months of validity at all times. BC permits, Alaska operations, and Pacific Rim port calls can require a passport with very little notice.",
      },
      {
        icon: "ri-ship-2-line",
        title: "Mexico & Alaska Cruises",
        subtitle: "Cruise travel is the most common international urgency for Newport coast residents",
        description: "Mexico Pacific coast cruises from Los Angeles (Puerto Vallarta, Mazatlan, Cabo) and Alaska Inside Passage cruises from Seattle are extremely popular with Newport-area residents. These cruises are booked months in advance — but passport expiration is rarely checked at booking time. Newport's coastal lifestyle means passports often sit unused for years. Fast Passport Center expedites passports for Newport-area cruise travelers in 2–9 business days.",
        tip: "Newport cruise travelers: the day you book an Alaska or Mexico cruise, put a calendar reminder to check your passport 10 months before departure. Alaska cruises in July should have passports checked in September of the previous year.",
      },
      {
        icon: "ri-sun-line",
        title: "Mexico & Costa Rica Winter Travel",
        subtitle: "Central coast residents escape to Mexico and Costa Rica November through April",
        description: "Newport winters are cold, wet, and dramatically beautiful — but many Newport residents escape to Mexico and Costa Rica from November through April. Puerto Vallarta, Oaxaca, and Cabo are popular from Portland. Costa Rica eco-adventure — OHSU research-connected Costa Rica trips, nature guide certification programs, and conservation volunteer work — is common with Newport's environmental community. These winter bookings happen in September–October, which is when passport expiration is commonly discovered.",
        tip: "Newport winter travel bookings: check your passport immediately when booking Mexico or Costa Rica travel in October. If it expires before June of next year, renew now. Fast Passport Center expedites via Corvallis FedEx in 2–9 business days.",
      },
    ],
    callout: {
      icon: "ri-water-flash-line",
      title: "Newport, Oregon — Where Science Meets the Pacific",
      description: "Newport is unlike any other Oregon coast city — a working commercial fishing port, a world-class marine research hub, and a beloved coastal arts community all in one. The HMSC research community, fishing industry professionals, and outdoor enthusiasts create a distinctive mix of international travel urgency. Fast Passport Center has served Newport and Lincoln County residents with expedited passports for marine research travel, fishing industry trips, Mexico cruises, and BC adventures.",
    },
  },
  destinations: {
    title: "Where Newport Travelers Go — And Why Passports Get Urgent",
    intro: "These are the destinations and travel patterns we see most from Newport and Lincoln County clients. Marine research travel, fishing industry urgency, cruise culture, and winter tropical escapes create distinctive passport needs for Oregon's oceanographic capital.",
    stats: [
      { stat: "50 mi", label: "to Corvallis FedEx via US-20 — fastest overnight shipping route from Newport" },
      { stat: "335 mi", label: "to Seattle Passport Agency — the nearest regional passport office" },
      { stat: "130 mi", label: "to Portland International Airport (PDX) — primary international hub" },
      { stat: "2–9", label: "business days for Fast Passport Center expedited processing from Newport" },
    ],
    destinations: [
      { name: "Alaska — Inside Passage Cruises & Fisheries", context: "Alaska cruises from Seattle and commercial fishing industry travel — Alaska is the most common international urgency for Newport", urgency: "Cruise bookings 6–12 months ahead; passport issues discovered at final payment or boarding document stage", icon: "ri-ship-2-line" },
      { name: "Japan & Pacific Rim — Marine Research", context: "HMSC and OSU researchers travel to Japan, New Zealand, Australia, and South Korea for conferences and fieldwork partnerships", urgency: "Conference acceptances can arrive with 3–6 weeks notice — immediate passport renewal needed upon confirmation", icon: "ri-flask-line" },
      { name: "Mexico — Puerto Vallarta, Oaxaca, Cabo", context: "Winter warm-weather escapes from Newport's cold, wet coast — November through April Mexico beach and cultural travel", urgency: "October Mexico bookings; passport expired discovered at document gathering in November–December", icon: "ri-sun-line" },
      { name: "British Columbia — Vancouver, Tofino, Whistler", context: "Pacific Northwest coastal culture and outdoor adventure — Tofino BC surfing, Whistler skiing, and Gulf Islands kayaking", urgency: "Tofino trips booked in summer; Whistler ski season in November — passport expired discovered at booking time", icon: "ri-map-pin-2-line" },
      { name: "Costa Rica & Galapagos", context: "Eco-adventure, conservation volunteer work, and marine biology field studies from Newport's ocean science community", urgency: "Research program acceptances and eco-tours can require passports on 2–4 weeks notice", icon: "ri-leaf-line" },
      { name: "Norway & Scandinavia", context: "Marine industry travel for Newport's fishing community — Norwegian and Scandinavian fisheries partnerships, Nordic heritage travel", urgency: "Industry conference invitations arrive 6–8 weeks before event — immediate passport renewal needed upon invitation", icon: "ri-global-line" },
    ],
    crossLinksTitle: "Explore Nearby Oregon Service Areas",
    crossLinksDesc: "We serve travelers throughout the central Oregon coast and Willamette Valley with expedited passport expertise.",
    crossLinks: [
      { name: "Lincoln City", route: "/cities/lincoln-city" },
      { name: "Corvallis", route: "/cities/corvallis" },
      { name: "Eugene", route: "/cities/eugene" },
    ],
  },
  vitalRecordsIcon: "ri-heart-pulse-line",
  vitalRecordsLinks: [
    { icon: "ri-file-text-line", label: "Birth & Death Certificates", desc: "Oregon Vital Records in Portland — order certified birth and death certificates for Lincoln County residents online, by mail, or in person for passport applications.", url: "https://www.oregon.gov/oha/PH/BirthDeathCertificates/Pages/index.aspx" },
    { icon: "ri-heart-line", label: "Marriage Records", desc: "Lincoln County Clerk — certified marriage licenses for name change passport applications and legal documentation.", url: "https://www.co.lincoln.or.us/clerk" },
    { icon: "ri-scales-line", label: "Divorce Decrees", desc: "Lincoln County Circuit Court — certified divorce decrees supporting passport name change or correction.", url: "https://www.courts.oregon.gov/courts/lincoln/" },
    { icon: "ri-global-line", label: "VitalChek — Nationwide Records", desc: "If your record was issued outside Oregon, order certified copies online through VitalChek for delivery in 5–10 business days.", url: "https://www.vitalchek.com/" },
  ],
};